import {scrubLinkToken} from '../components/connection_links/scrubLinkToken';

/**
 * Un `Failed to fetch` del navegador del usuario NO es una señal de que el backend esté caído.
 *
 * Existe porque el microcorte del 2026-09-15 metió 13 issues distintos del mismo instante, todos
 * con el mismo culprit, y ese ruido tapa lo que sí hay que mirar.
 *
 * Las dos situaciones NO se tratan igual, porque no son igual de confiables:
 *
 *  - **Sin conexión** (`navigator.onLine === false`) es señal sólida: el navegador afirma que no
 *    hay red. Ese evento se DESCARTA.
 *  - **Pestaña en segundo plano** (`document.hidden`) es señal floja: esta app sí pide datos de
 *    fondo (el poll de entidades cada 10s, los refrescos por WebSocket), así que una caída real que
 *    golpee sobre todo a pestañas de fondo existe. Ese evento se MARCA y se manda igual, con el
 *    nivel bajado: descartarlo no dejaría ni el conteo, y un incidente invisible es peor que uno
 *    ruidoso. Filtrar por el tag `conexion_en_background` en Sentry da el mismo silencio, pero
 *    reversible.
 */
const REGEX_FALLO_DE_RED = /Failed to fetch|NetworkError when attempting to fetch|Load failed/;

function esFalloDeRedDelNavegador(hint) {
  const error = hint && hint.originalException;
  return REGEX_FALLO_DE_RED.test((error && error.message) || '');
}

/**
 * Una ventana donde el QA en producción trucó el `fetch` no manda nada a Sentry.
 *
 * El kit del QA (`~/.claude/tools/qa/qa-stub.js`, en la sesión de fede) arma los estados de un caso
 * con stubs de `fetch`: un 500 simulado, un fallo de red, una respuesta mutada. Todo lo que eso
 * produce sale por el `APIClient` como un error más («HTTP 500 en …», «Failed to fetch») y no se
 * distingue de uno real: en el barrido de Sentry del 26/10 fueron FRONTEND-AT, AV, AR, AG y AS, y
 * 64 de los 90 «Failed to fetch» de 72 h. Por eso el kit marca la ventana al instalar el stub
 * (`window.__DASHBO_QA_STUB__ = true`) y acá se descarta TODO evento de esa ventana, no sólo los de
 * red: un componente que se rompe con una respuesta mutada tampoco es un error de un usuario.
 * La marca vive hasta que la ventana se recarga; una pestaña de un usuario no la tiene nunca.
 */
export function esVentanaDelQA(win = window) {
  return win.__DASHBO_QA_STUB__ === true;
}

/** El `beforeSend` de `Sentry.init` (`src/index.js`). */
export function sentryBeforeSend(event, hint) {
  if (esVentanaDelQA()) return null;

  // El link de Mercado Ads para el vendedor lleva su token en la URL (`/conectar/<token>`): con él,
  // cualquiera conecta una cuenta a ese cliente. Ni el pedido ni las migas lo mandan a Sentry.
  scrubLinkToken(event);
  if (!esFalloDeRedDelNavegador(hint)) return event;

  if (!navigator.onLine) return null;

  if (document.hidden) {
    event.tags = {...(event.tags || {}), conexion_en_background: true};
    event.level = 'info';
  }
  return event;
}
